import React from 'react';
import styles from './BranchingOpsSection.module.css';
import Ico from '../common/Ico';
import Pill from '../common/Pill';
import { statusLabel, statusTokens } from './statusConfig';

const ICO_BRANCH    = 'M6 3v12 M18 9a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M6 21a3 3 0 1 0 0-6 3 3 0 0 0 0 6z M18 9a9 9 0 0 1-9 9';
const ICO_CHEVRIGHT = 'm9 18 6-6-6-6';
const ICO_CHEVDOWN  = 'm6 9 6 6 6-6';

// Returns { svgEl: JSX, bg: hex } matching the design's srcGlyph() logic.
// Order of checks mirrors design: openmetadata → collate → helm → terraform → hybrid+runner → ai → fallback
export function getComponentGlyph(name) {
  const n = (name || '').toLowerCase();

  if (n.includes('openmetadata')) return {
    bg: '#F4F3FF',
    svgEl: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <ellipse cx="12" cy="5.6" rx="7" ry="2.6" stroke="#7C3AED"/>
        <path d="M5 5.6v12.8c0 1.44 3.13 2.6 7 2.6s7-1.16 7-2.6V5.6" stroke="#7C3AED"/>
        <path d="M8.4 17V11.4l3.6 3.1 3.6-3.1V17" stroke="#A78BFA"/>
      </svg>
    ),
  };

  if (n === 'collate') return {
    bg: '#EFF8FF',
    svgEl: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3.5 7 12 11.2 20.5 7" stroke="#00C2FF"/>
        <path d="M3.5 12 12 16.2 20.5 12" stroke="#2E90FA"/>
        <path d="M3.5 17 12 21.2 20.5 17" stroke="#1570EF"/>
      </svg>
    ),
  };

  if (n.includes('helm')) return {
    bg: '#F5F5F5',
    svgEl: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#535862" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9"/>
        <circle cx="12" cy="12" r="2.6"/>
        <path d="M12 3v4M12 17v4M3 12h4M17 12h4M5.6 5.6l2.8 2.8M15.6 15.6l2.8 2.8M18.4 5.6l-2.8 2.8M8.4 15.6l-2.8 2.8"/>
      </svg>
    ),
  };

  if (n.includes('terraform')) return {
    bg: '#EEF4FF',
    svgEl: (
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6172F3" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 7l8-4 8 4-8 4-8-4zM4 7v6l8 4M20 7v6l-8 4"/>
      </svg>
    ),
  };

  if (n.includes('hybrid') && n.includes('runner')) return {
    bg: '#EFF8FF',
    svgEl: (
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#175CD3" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 4h20v6H2zM2 14h20v6H2zM6 7h.01M6 17h.01"/>
      </svg>
    ),
  };

  if (n.includes('construct')) return {
    bg: '#EFF8FF',
    svgEl: (
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#1570EF" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 8h7V3M14 3v7h7M21 16h-7v5M10 21v-5H3"/>
      </svg>
    ),
  };

  if (n.includes('ai')) return {
    bg: '#F4F3FF',
    svgEl: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="#5925DC" stroke="none">
        <path d="m12 3 1.5 5L19 9.5 14 11l-2 5-2-5-5-1.5L10 8z"/>
      </svg>
    ),
  };

  return {
    bg: '#F5F5F5',
    svgEl: (
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#667085" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d="m21 16-9 5-9-5V8l9-5 9 5v8zM3.3 7 12 12l8.7-5M12 22V12"/>
      </svg>
    ),
  };
}

export const COMPONENT_ORDER = [
  'openmetadata',
  'collate',
  'openmetadata-helm-charts',
  'hybrid-runner',
  'hybrid-helm',
  'terraform-aws',
  'terraform-deploy',
  'ai-platform',
  'ai-proxy-helm',
  'construct',
];

export default function BranchingOpsSection({ branch, onOpenOp }) {
  const ops = Object.entries(branch.branching_ops || {});
  const shouldExpand = branch.status === 'branching';
  const [expanded, setExpanded] = React.useState(shouldExpand);

  React.useEffect(() => {
    setExpanded(shouldExpand);
  }, [shouldExpand]);

  if (!ops.length) return null;

  const opList = ops
    .map(([component, op]) => ({ component, ...op }))
    .sort((a, b) => {
      const ia = COMPONENT_ORDER.indexOf(a.component);
      const ib = COMPONENT_ORDER.indexOf(b.component);
      return (ia === -1 ? Infinity : ia) - (ib === -1 ? Infinity : ib);
    });
  const done    = opList.filter(o => o.status === 'completed').length;
  const allDone = done === opList.length;

  return (
    <section className={styles.section} id="sec-branching">
      <div className={styles.header}>
        <span className={styles.iconTile}>
          <Ico d={ICO_BRANCH} size={16} strokeWidth={1.7} />
        </span>
        <div className={styles.headerText}>
          <div className={styles.title}>Branching operations</div>
          <div className={styles.subtitle}>
            Component repos branched from <code className={styles.code}>main</code> when the branch was created.
          </div>
        </div>
        <Pill kind={allDone ? 'ok' : done > 0 ? 'warn' : 'neutral'} dot>
          {done} of {opList.length} branched
        </Pill>
        <button
          className={`${styles.toggleBtn}${expanded ? ` ${styles.toggleBtnOpen}` : ''}`}
          type="button"
          aria-label={expanded ? 'Collapse branching operations' : 'Expand branching operations'}
          title={expanded ? 'Collapse' : 'Expand'}
          onClick={() => setExpanded((v) => !v)}
        >
          <Ico d={ICO_CHEVDOWN} size={14} strokeWidth={2} />
        </button>
      </div>

      {expanded && (
        <div className={styles.grid}>
          {opList.map((op) => {
            const glyph  = getComponentGlyph(op.component);
            const tokens = statusTokens(op.status);
            return (
              <button
                key={op.component}
                className={styles.opCard}
                onClick={() => onOpenOp?.({ component: op.component, op })}
                type="button"
              >
                <span className={styles.opIconTile} style={{ background: glyph.bg }}>
                  {glyph.svgEl}
                </span>
                <span className={styles.opContent}>
                  <span className={styles.opName}>{op.component}</span>
                  <span className={styles.opStatus}>
                    <span className={styles.opDot} style={{ background: tokens?.dot }} />
                    <span className={styles.opStatusText}>{statusLabel(op.status)}</span>
                  </span>
                </span>
                <Ico d={ICO_CHEVRIGHT} size={13} style={{ color: 'var(--gray-400)', flexShrink: 0 }} />
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}
